// ============================================
// COSMIC HINDU - Cart Context
// ============================================

import { createContext, useContext, useReducer, useCallback } from 'react';
import { SHIPPING } from '@config/constants';

const CartContext = createContext(null);

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_ITEM': {
      // Use cartKey (product id + variant combo) for uniqueness, fallback to id
      const key = action.payload.cartKey || action.payload.id;
      const exists = state.items.find((i) => (i.cartKey || i.id) === key);
      if (exists) {
        return {
          ...state,
          items: state.items.map((i) =>
            (i.cartKey || i.id) === key
              ? { ...i, qty: i.qty + (action.payload.qty || 1) }
              : i
          ),
        };
      }
      return {
        ...state,
        items: [...state.items, { ...action.payload, qty: action.payload.qty || 1 }],
      };
    }
    case 'REMOVE_ITEM':
      return { ...state, items: state.items.filter((i) => (i.cartKey || i.id) !== action.payload) };
    case 'UPDATE_QTY':
      return {
        ...state,
        items: state.items.map((i) =>
          (i.cartKey || i.id) === action.payload.id
            ? { ...i, qty: Math.max(1, action.payload.qty) }
            : i
        ),
      };
    case 'CLEAR_CART':
      return { ...state, items: [] };
    default:
      return state;
  }
};

export function CartProvider({ children }) {
  const [state, dispatch] = useReducer(cartReducer, { items: [] });

  const addItem = useCallback((product, qty = 1) => {
    dispatch({ type: 'ADD_ITEM', payload: { ...product, qty } });
  }, []);

  const removeItem = useCallback((id) => {
    dispatch({ type: 'REMOVE_ITEM', payload: id });
  }, []);

  const updateQty = useCallback((id, qty) => {
    dispatch({ type: 'UPDATE_QTY', payload: { id, qty } });
  }, []);

  const clearCart = useCallback(() => {
    dispatch({ type: 'CLEAR_CART' });
  }, []);

  const itemCount = state.items.reduce((sum, i) => sum + i.qty, 0);
  const subtotal = state.items.reduce((sum, i) => sum + i.price * i.qty, 0);
  const shipping = subtotal >= SHIPPING.freeThreshold ? 0 : SHIPPING.standardRate;
  const total = subtotal + shipping;

  const value = {
    items: state.items,
    itemCount,
    subtotal,
    shipping,
    total,
    addItem,
    removeItem,
    updateQty,
    clearCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};

export default CartContext;
